import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api';

const pageStyle = {
  minHeight: '100vh',
  background:
    'radial-gradient(circle at top left, rgba(59,130,246,0.20), transparent 32%), radial-gradient(circle at top right, rgba(34,197,94,0.12), transparent 28%), linear-gradient(180deg, #020617 0%, #0f172a 42%, #111827 100%)',
  color: '#e5e7eb',
};

const shellStyle = {
  maxWidth: 1800,
  margin: '0 auto',
};

const cardStyle = {
  background: 'rgba(15, 23, 42, 0.86)',
  border: '1px solid rgba(148, 163, 184, 0.16)',
  borderRadius: 22,
  color: '#e5e7eb',
  boxShadow: '0 18px 45px rgba(0,0,0,0.28)',
  backdropFilter: 'blur(10px)',
};

const cardSoftStyle = {
  background: 'rgba(30, 41, 59, 0.82)',
  border: '1px solid rgba(148, 163, 184, 0.14)',
  borderRadius: 20,
  color: '#e5e7eb',
};

const heroStyle = {
  background:
    'linear-gradient(135deg, rgba(37,99,235,0.35), rgba(15,23,42,0.94) 42%, rgba(22,163,74,0.20))',
  border: '1px solid rgba(148, 163, 184, 0.18)',
  borderRadius: 28,
  color: '#f8fafc',
  boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
};

const inputStyle = {
  background: '#020617',
  color: '#e5e7eb',
  border: '1px solid rgba(148, 163, 184, 0.35)',
  borderRadius: 12,
};

const mutedStyle = {
  color: '#94a3b8',
};

const pillStyle = {
  border: '1px solid rgba(148, 163, 184, 0.20)',
  background: 'rgba(15, 23, 42, 0.72)',
  color: '#cbd5e1',
  borderRadius: 999,
  padding: '6px 11px',
  fontSize: 12,
  fontWeight: 700,
};

const formatDate = (value) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString();
};

const safeTime = (value) => {
  if (!value) return 0;

  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

export default function Purchases() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [copiedOrderId, setCopiedOrderId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [scanFilter, setScanFilter] = useState('all');
  const [error, setError] = useState('');

  const isVerified = (row) => Number(row.tracking_verified) === 1;

  const isDelivered = (row) => {
    return String(row.status || '').toLowerCase() === 'delivered';
  };

  const getRelatedSkus = (row) => {
    let skus = [];

    if (Array.isArray(row.related_skus)) {
      skus = row.related_skus;
    } else if (typeof row.related_skus === 'string' && row.related_skus.trim()) {
      try {
        const parsed = JSON.parse(row.related_skus);
        skus = Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        skus = row.related_skus
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }

    if (
      skus.length === 0 &&
      typeof row.related_skus_raw === 'string' &&
      row.related_skus_raw.trim()
    ) {
      skus = row.related_skus_raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }

    if (row.fifo_sku && !skus.includes(row.fifo_sku)) {
      skus.unshift(row.fifo_sku);
    }

    return [...new Set(skus.map((s) => String(s).trim()).filter(Boolean))];
  };

  const sortPurchases = (rows) => {
    return [...rows].sort((a, b) => {
      const aVerified = isVerified(a) ? 1 : 0;
      const bVerified = isVerified(b) ? 1 : 0;

      const aScanTime = safeTime(a.tracking_verified_at);
      const bScanTime = safeTime(b.tracking_verified_at);

      if (aVerified !== bVerified) return bVerified - aVerified;
      if (aScanTime !== bScanTime) return bScanTime - aScanTime;

      const aDelivered = isDelivered(a) ? 1 : 0;
      const bDelivered = isDelivered(b) ? 1 : 0;

      if (aDelivered !== bDelivered) return bDelivered - aDelivered;

      return safeTime(b.updated_at) - safeTime(a.updated_at);
    });
  };

  const fetchData = async ({ firstLoad = false } = {}) => {
    try {
      if (firstLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError('');

      const res = await axios.get('/purchases');
      const rows = Array.isArray(res.data) ? res.data : [];

      setData(sortPurchases(rows));
    } catch (err) {
      console.error('Failed to load purchases', err);
      setError('Failed to load purchases. Check your backend logs or try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData({ firstLoad: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyText = async (text, label = 'Text') => {
    const cleanText = String(text || '').trim();

    if (!cleanText) {
      alert(`No ${label.toLowerCase()} found to copy.`);
      return false;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(cleanText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = cleanText;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      return true;
    } catch (err) {
      console.error(`Failed to copy ${label}`, err);
      alert(`Failed to copy ${label.toLowerCase()}.`);
      return false;
    }
  };

  const handleCopyOrderId = async (orderId) => {
    const cleanOrderId = String(orderId || '').trim();

    const copied = await copyText(cleanOrderId, 'Order number');

    if (copied) {
      setCopiedOrderId(cleanOrderId);

      window.setTimeout(() => {
        setCopiedOrderId((current) => {
          return current === cleanOrderId ? null : current;
        });
      }, 1400);
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm(`Delete purchase ${orderId}?`)) return;

    try {
      setDeletingOrderId(orderId);
      await axios.delete(`/purchases/${orderId}`);

      setData((prev) => prev.filter((p) => p.order_id !== orderId));
    } catch (err) {
      console.error('Delete failed', err);
      alert('Failed to delete order');
    } finally {
      setDeletingOrderId(null);
    }
  };

  const openRelatedSkuTabs = (row) => {
    const skus = getRelatedSkus(row);

    console.log('Clicked purchase row:', row);
    console.log('Order ID:', row.order_id);
    console.log('related_skus:', row.related_skus);
    console.log('related_skus_raw:', row.related_skus_raw);
    console.log('fifo_sku:', row.fifo_sku);
    console.log('SKUs to open:', skus);

    if (!skus.length) {
      alert('No FIFO SKU match found for this order.');
      return;
    }

    const openedTabs = skus.map((sku, index) => {
      return window.open(
        'about:blank',
        `fifo_${String(row.order_id).replace(/[^a-zA-Z0-9]/g, '_')}_${index}`
      );
    });

    let blockedCount = 0;

    openedTabs.forEach((tab, index) => {
      const sku = skus[index];
      const url = `/edit/${encodeURIComponent(sku)}?fifo_order=${encodeURIComponent(row.order_id)}`;

      if (tab) {
        tab.location.href = url;
      } else {
        blockedCount++;
        console.warn('Popup blocked for SKU:', sku);
      }
    });

    if (blockedCount > 0) {
      alert(
        `${blockedCount} tab(s) were blocked by the browser. Allow popups for thornsdashboard.com, then try again.`
      );
    }
  };

  const CopyOrderButton = ({ orderId }) => {
    const cleanOrderId = String(orderId || '').trim();
    const copied = cleanOrderId && copiedOrderId === cleanOrderId;

    if (!cleanOrderId) {
      return null;
    }

    return (
      <button
        type="button"
        className="btn btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleCopyOrderId(cleanOrderId);
        }}
        title="Copy order number"
        style={{
          padding: '3px 8px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 900,
          lineHeight: 1.2,
          background: copied ? 'rgba(34,197,94,0.20)' : 'rgba(59,130,246,0.14)',
          color: copied ? '#86efac' : '#bfdbfe',
          border: copied
            ? '1px solid rgba(34,197,94,0.38)'
            : '1px solid rgba(59,130,246,0.32)',
          whiteSpace: 'nowrap',
        }}
      >
        {copied ? 'Copied ✓' : 'Copy'}
      </button>
    );
  };

  const StatusBadge = ({ status }) => {
    const cleanStatus = String(status || 'Pending');

    if (cleanStatus === 'Delivered') {
      return (
        <span
          className="badge"
          style={{
            background: 'rgba(34,197,94,0.18)',
            color: '#86efac',
            border: '1px solid rgba(34,197,94,0.28)',
          }}
        >
          Delivered
        </span>
      );
    }

    if (cleanStatus === 'Shipped') {
      return (
        <span
          className="badge"
          style={{
            background: 'rgba(59,130,246,0.18)',
            color: '#93c5fd',
            border: '1px solid rgba(59,130,246,0.28)',
          }}
        >
          Shipped
        </span>
      );
    }

    if (cleanStatus === 'Out for Delivery') {
      return (
        <span
          className="badge"
          style={{
            background: 'rgba(251,191,36,0.18)',
            color: '#fcd34d',
            border: '1px solid rgba(251,191,36,0.28)',
          }}
        >
          Out for Delivery
        </span>
      );
    }

    return (
      <span
        className="badge"
        style={{
          background: 'rgba(148,163,184,0.16)',
          color: '#cbd5e1',
          border: '1px solid rgba(148,163,184,0.25)',
        }}
      >
        Pending
      </span>
    );
  };

  const SourceBadge = ({ source }) => {
    const cleanSource = String(source || 'eBay');

    if (cleanSource.toLowerCase() === 'tcgplayer') {
      return (
        <span
          className="badge"
          style={{
            background: 'rgba(56,189,248,0.16)',
            color: '#67e8f9',
            border: '1px solid rgba(56,189,248,0.28)',
          }}
        >
          TCGPlayer
        </span>
      );
    }

    return (
      <span
        className="badge"
        style={{
          background: 'rgba(148,163,184,0.14)',
          color: '#e5e7eb',
          border: '1px solid rgba(148,163,184,0.22)',
        }}
      >
        eBay
      </span>
    );
  };

  const CarrierBadge = ({ carrier }) => {
    const cleanCarrier = String(carrier || '').trim();

    if (!cleanCarrier) {
      return <span style={{ color: '#64748b' }}>—</span>;
    }

    const lower = cleanCarrier.toLowerCase();

    if (lower === 'usps') {
      return (
        <span
          className="badge"
          style={{
            background: 'rgba(255,255,255,0.12)',
            color: '#f8fafc',
            border: '1px solid rgba(255,255,255,0.22)',
          }}
        >
          USPS
        </span>
      );
    }

    if (lower === 'ups') {
      return (
        <span
          className="badge"
          style={{
            background: 'rgba(59,130,246,0.18)',
            color: '#93c5fd',
            border: '1px solid rgba(59,130,246,0.28)',
          }}
        >
          UPS
        </span>
      );
    }

    if (lower === 'fedex') {
      return (
        <span
          className="badge"
          style={{
            background: 'rgba(251,191,36,0.18)',
            color: '#fcd34d',
            border: '1px solid rgba(251,191,36,0.28)',
          }}
        >
          FedEx
        </span>
      );
    }

    return (
      <span
        className="badge"
        style={{
          background: 'rgba(148,163,184,0.16)',
          color: '#cbd5e1',
          border: '1px solid rgba(148,163,184,0.25)',
        }}
      >
        {cleanCarrier}
      </span>
    );
  };

  const MiniMetric = ({ label, value, color = '#e5e7eb', icon }) => {
    return (
      <div
        style={{
          padding: '13px 14px',
          borderRadius: 18,
          background: 'rgba(2, 6, 23, 0.45)',
          border: '1px solid rgba(148, 163, 184, 0.12)',
          height: '100%',
        }}
      >
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>
              {label}
            </div>
            <div style={{ color, fontWeight: 900, fontSize: 24, lineHeight: 1 }}>
              {value}
            </div>
          </div>

          {icon && (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 38,
                height: 38,
                borderRadius: 14,
                background: `${color}22`,
                border: `1px solid ${color}35`,
                fontSize: 19,
              }}
            >
              {icon}
            </div>
          )}
        </div>
      </div>
    );
  };

  const trackingDisplay = (row) => {
    const verified = isVerified(row);
    const tracking = row.tracking_number;

    if (tracking) {
      return (
        <div>
          <div
            style={{
              color: verified ? '#86efac' : '#e5e7eb',
              fontWeight: verified ? 900 : 800,
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 13,
              wordBreak: 'break-word',
            }}
          >
            {verified ? '✅ ' : ''}
            {tracking}
          </div>

          {verified && row.tracking_scan_raw && (
            <div
              style={{
                color: '#86efac',
                fontSize: 11,
                marginTop: 4,
                wordBreak: 'break-word',
                opacity: 0.88,
              }}
            >
              Raw scan: {row.tracking_scan_raw}
            </div>
          )}
        </div>
      );
    }

    if (row.carrier) {
      return <span style={{ color: '#94a3b8' }}>{row.carrier} via eBay</span>;
    }

    return <span style={{ color: '#64748b' }}>No tracking</span>;
  };

  const uniqueStatuses = useMemo(() => {
    const statuses = new Set();

    data.forEach((row) => {
      statuses.add(String(row.status || 'Pending'));
    });

    return Array.from(statuses).sort();
  }, [data]);

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return sortPurchases(data).filter((row) => {
      const verified = isVerified(row);
      const rowStatus = String(row.status || 'Pending');
      const relatedSkus = getRelatedSkus(row);

      if (statusFilter !== 'all' && rowStatus !== statusFilter) return false;
      if (scanFilter === 'scanned' && !verified) return false;
      if (scanFilter === 'not_scanned' && verified) return false;

      if (!term) return true;

      const haystack = [
        row.order_id,
        row.title,
        row.carrier,
        row.tracking_number,
        row.status,
        row.source,
        row.tracking_scan_raw,
        ...relatedSkus,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchTerm, statusFilter, scanFilter]);

  const stats = useMemo(() => {
    const total = data.length;
    const scanned = data.filter((row) => isVerified(row)).length;
    const delivered = data.filter((row) => isDelivered(row)).length;
    const deliveredNotScanned = data.filter(
      (row) => isDelivered(row) && !isVerified(row)
    ).length;
    const fifoLinked = data.filter((row) => getRelatedSkus(row).length > 0).length;

    return {
      total,
      scanned,
      delivered,
      deliveredNotScanned,
      fifoLinked,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading) {
    return (
      <div style={pageStyle}>
        <div className="container-fluid py-4" style={shellStyle}>
          <div className="p-4" style={cardStyle}>
            <div className="d-flex align-items-center gap-3">
              <div className="spinner-border text-info" role="status" />
              <div>
                <h4 className="mb-1" style={{ color: '#f8fafc' }}>
                  Loading Purchases
                </h4>
                <p className="mb-0" style={mutedStyle}>
                  Pulling delivered backfill records, tracking numbers, FIFO links, and scan status.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <style>
        {`
          .purchases-dark input,
          .purchases-dark select,
          .purchases-dark textarea {
            background-color: #020617 !important;
            color: #e5e7eb !important;
            border-color: rgba(148, 163, 184, 0.35) !important;
          }

          .purchases-dark input::placeholder,
          .purchases-dark textarea::placeholder {
            color: #64748b !important;
          }

          .purchases-dark input:focus,
          .purchases-dark select:focus,
          .purchases-dark textarea:focus {
            box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.18) !important;
            border-color: rgba(56, 189, 248, 0.75) !important;
          }

          .purchases-dark .table {
            --bs-table-bg: transparent;
            --bs-table-color: #e5e7eb;
            --bs-table-border-color: rgba(148, 163, 184, 0.12);
            color: #e5e7eb;
          }

          .purchases-dark .table thead th {
            color: #cbd5e1;
            background: rgba(15, 23, 42, 0.94) !important;
            border-bottom: 1px solid rgba(148, 163, 184, 0.18);
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.6px;
            white-space: nowrap;
          }

          .purchases-dark .table tbody tr {
            border-color: rgba(148, 163, 184, 0.10);
          }

          .purchases-dark .table tbody tr:hover {
            background: rgba(30, 41, 59, 0.48) !important;
          }

          .purchases-dark .form-label {
            color: #cbd5e1;
            font-size: 13px;
            font-weight: 700;
          }

          .purchases-dark .btn {
            border-radius: 12px;
            font-weight: 700;
          }

          .purchases-dark .alert {
            border-radius: 18px;
          }
        `}
      </style>

      <div className="container-fluid py-4 purchases-dark" style={shellStyle}>
        <div className="p-4 p-lg-5 mb-4" style={heroStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span style={{ ...pillStyle, color: '#bfdbfe', borderColor: 'rgba(59,130,246,0.38)' }}>
                  Package Tracking
                </span>
                <span style={{ ...pillStyle, color: '#bbf7d0', borderColor: 'rgba(34,197,94,0.38)' }}>
                  Delivered Backfill
                </span>
                <span style={{ ...pillStyle, color: '#fef3c7', borderColor: 'rgba(245,158,11,0.38)' }}>
                  Barcode Verified
                </span>
                <span style={{ ...pillStyle, color: '#fde68a', borderColor: 'rgba(251,191,36,0.38)' }}>
                  FIFO Linked
                </span>
              </div>

              <h1
                className="mb-3"
                style={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  letterSpacing: '-0.04em',
                  fontSize: 'clamp(2rem, 4vw, 4.35rem)',
                  lineHeight: 0.95,
                }}
              >
                Purchases Control Center
              </h1>

              <p className="mb-0" style={{ color: '#cbd5e1', maxWidth: 900, fontSize: 17 }}>
                Track delivered eBay purchases, match physical package barcodes, and jump straight into linked FIFO batches.
                The newest scanned package stays pinned to the top.
              </p>
            </div>

            <div
              className="p-3"
              style={{
                ...cardSoftStyle,
                minWidth: 290,
              }}
            >
              <div style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Current View
              </div>

              <div style={{ color: '#f8fafc', fontSize: 32, fontWeight: 950, lineHeight: 1 }}>
                {filteredData.length}
              </div>

              <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>
                purchases showing from {data.length} total rows
              </div>

              <div className="d-grid gap-2 mt-3">
                <button
                  className="btn btn-info"
                  onClick={() => fetchData()}
                  disabled={refreshing}
                >
                  {refreshing ? 'Refreshing...' : 'Refresh Purchases'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div
            className="alert mb-4"
            style={{
              background: 'rgba(127,29,29,0.70)',
              border: '1px solid rgba(248,113,113,0.35)',
              color: '#fee2e2',
            }}
          >
            <strong>Purchases could not load.</strong>
            <div>{error}</div>
            <button className="btn btn-sm btn-light mt-2" onClick={() => fetchData()}>
              Retry
            </button>
          </div>
        )}

        <div className="row mb-4">
          <div className="col-md-6 col-xl-3 mb-3">
            <MiniMetric label="Total Purchases" value={stats.total} color="#f8fafc" icon="📦" />
          </div>

          <div className="col-md-6 col-xl-3 mb-3">
            <MiniMetric label="Scanned / Verified" value={stats.scanned} color="#86efac" icon="✅" />
          </div>

          <div className="col-md-6 col-xl-3 mb-3">
            <MiniMetric label="Delivered" value={stats.delivered} color="#93c5fd" icon="🚚" />
          </div>

          <div className="col-md-6 col-xl-3 mb-3">
            <MiniMetric label="Delivered Not Scanned" value={stats.deliveredNotScanned} color="#fcd34d" icon="⚠️" />
          </div>
        </div>

        <div className="p-4 mb-4" style={cardStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                Search & Filters
              </h4>
              <p className="mb-0" style={mutedStyle}>
                Search by order ID, tracking number, title, carrier, or linked FIFO SKU.
              </p>
            </div>

            <span style={{ ...pillStyle, color: '#86efac', borderColor: 'rgba(34,197,94,0.35)' }}>
              {stats.fifoLinked} FIFO linked
            </span>
          </div>

          <div className="row align-items-end">
            <div className="col-lg-5 mb-3">
              <label className="form-label">Search</label>
              <input
                className="form-control"
                style={inputStyle}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Order ID, tracking, SKU, title..."
              />
            </div>

            <div className="col-md-4 col-lg-3 mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                style={inputStyle}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All statuses</option>
                {uniqueStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 col-lg-3 mb-3">
              <label className="form-label">Scan Status</label>
              <select
                className="form-select"
                style={inputStyle}
                value={scanFilter}
                onChange={(e) => setScanFilter(e.target.value)}
              >
                <option value="all">All scans</option>
                <option value="scanned">Scanned only</option>
                <option value="not_scanned">Not scanned only</option>
              </select>
            </div>

            <div className="col-md-4 col-lg-1 mb-3">
              <button
                className="btn btn-outline-light w-100"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setScanFilter('all');
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 mb-4" style={cardStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                ✅ Scan Matching Rules
              </h4>
              <p className="mb-0" style={mutedStyle}>
                Green rows mean the barcode was scanned and matched against the saved tracking number.
                Click a linked order ID to open its FIFO SKU batch page, or use Copy to quickly copy the order number.
              </p>
            </div>

            <span style={{ ...pillStyle, color: '#bfdbfe', borderColor: 'rgba(59,130,246,0.38)' }}>
              Newest scan first
            </span>
          </div>
        </div>

        <div
          style={{
            ...cardStyle,
            overflow: 'hidden',
          }}
        >
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Verified</th>
                  <th>Source</th>
                  <th>Order / FIFO</th>
                  <th>Title</th>
                  <th>Carrier</th>
                  <th>Tracking</th>
                  <th>Status</th>
                  <th>Scanned At</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center" style={{ padding: '44px 16px' }}>
                      <div style={{ fontSize: 34 }}>📭</div>
                      <div style={{ color: '#f8fafc', fontWeight: 900, fontSize: 18 }}>
                        No purchases found
                      </div>
                      <div style={mutedStyle}>
                        Clear filters or run the delivered backfill scraper.
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row, index) => {
                    const verified = isVerified(row);
                    const delivered = isDelivered(row);
                    const relatedSkus = getRelatedSkus(row);
                    const relatedSkuCount = relatedSkus.length;
                    const rowDeleting = deletingOrderId === row.order_id;

                    return (
                      <tr
                        key={`${row.order_id || 'no-order'}-${index}`}
                        style={{
                          background: verified
                            ? 'rgba(22, 101, 52, 0.20)'
                            : delivered
                              ? 'rgba(30, 41, 59, 0.88)'
                              : 'rgba(15, 23, 42, 0.72)',
                          borderLeft: verified
                            ? '6px solid #22c55e'
                            : delivered
                              ? '6px solid #3b82f6'
                              : '6px solid transparent',
                        }}
                      >
                        <td>
                          {verified ? (
                            <span
                              className="badge"
                              style={{
                                background: 'rgba(34,197,94,0.18)',
                                color: '#86efac',
                                border: '1px solid rgba(34,197,94,0.28)',
                              }}
                            >
                              Scanned
                            </span>
                          ) : (
                            <span
                              className="badge"
                              style={{
                                background: delivered
                                  ? 'rgba(251,191,36,0.18)'
                                  : 'rgba(148,163,184,0.16)',
                                color: delivered ? '#fcd34d' : '#cbd5e1',
                                border: delivered
                                  ? '1px solid rgba(251,191,36,0.28)'
                                  : '1px solid rgba(148,163,184,0.25)',
                              }}
                            >
                              Not Scanned
                            </span>
                          )}
                        </td>

                        <td>
                          <SourceBadge source={row.source} />
                        </td>

                        <td style={{ minWidth: 250 }}>
                          {row.order_id ? (
                            relatedSkus.length > 0 ? (
                              <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <button
                                    type="button"
                                    onClick={() => openRelatedSkuTabs(row)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                      fontWeight: 900,
                                      color: '#60a5fa',
                                      cursor: 'pointer',
                                      fontFamily:
                                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                    }}
                                  >
                                    {row.order_id}
                                  </button>

                                  <CopyOrderButton orderId={row.order_id} />
                                </div>

                                <div
                                  style={{
                                    fontSize: 11,
                                    color: '#86efac',
                                    fontWeight: 800,
                                    marginTop: 3,
                                  }}
                                >
                                  Linked to FIFO batch
                                  {relatedSkuCount > 1 ? ` · ${relatedSkuCount} SKUs` : ''}
                                </div>

                                <div
                                  style={{
                                    fontSize: 11,
                                    color: '#94a3b8',
                                    marginTop: 3,
                                    maxWidth: 280,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {relatedSkus.join(', ')}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <strong
                                    style={{
                                      color: '#f8fafc',
                                      fontFamily:
                                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                    }}
                                  >
                                    {row.order_id}
                                  </strong>

                                  <CopyOrderButton orderId={row.order_id} />
                                </div>

                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                                  No FIFO batch match
                                </div>
                              </div>
                            )
                          ) : (
                            <div>
                              <strong style={{ color: '#64748b' }}>No order number</strong>
                              <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                                Nothing to copy
                              </div>
                            </div>
                          )}
                        </td>

                        <td style={{ minWidth: 280, maxWidth: 460 }}>
                          {row.title ? (
                            <div style={{ color: '#e5e7eb', fontWeight: 650 }}>
                              {row.title}
                            </div>
                          ) : (
                            <span style={{ color: '#64748b' }}>No title</span>
                          )}
                        </td>

                        <td>
                          <CarrierBadge carrier={row.carrier} />
                        </td>

                        <td style={{ minWidth: 220, maxWidth: 320 }}>
                          {trackingDisplay(row)}
                        </td>

                        <td>
                          <StatusBadge status={row.status} />
                        </td>

                        <td style={{ color: verified ? '#86efac' : '#94a3b8', whiteSpace: 'nowrap' }}>
                          {formatDate(row.tracking_verified_at)}
                        </td>

                        <td style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>
                          {formatDate(row.updated_at)}
                        </td>

                        <td>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(row.order_id)}
                            disabled={rowDeleting || !row.order_id}
                          >
                            {rowDeleting ? 'Deleting...' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-3" style={{ color: '#64748b', fontSize: 12 }}>
          Showing {filteredData.length} of {data.length} purchases. Scanned rows sort by latest scan time first.
        </div>
      </div>
    </div>
  );
}